/**
 * usePrinterFallback — hook for å håndtere "No printer configured"-feil
 * på en konsistent måte på tvers av utskriftsstier.
 *
 * Bruk:
 *   const { tryPrint, modal } = usePrinterFallback();
 *
 *   const handleClick = () => {
 *     tryPrint(
 *       () => printingService.printLabel(companyNo, request),
 *       (printerId) => printingService.printLabel(companyNo, { ...request, printerIdOverride: printerId }),
 *       (result) => { // håndter result her }
 *     );
 *   };
 *
 *   return <>{button}{modal}</>;
 *
 * Hvis første kall feiler med "No printer configured...", åpnes en modal hvor
 * brukeren kan velge skriver. Bekreftelse kjører retry-callback'en med valgt
 * PrintNode-ID som override. Resultatet (uansett vellykket eller ikke) sendes
 * til onResult-callback'en.
 */
import React, { useState } from 'react';
import { PrinterFallbackModal } from '../components/common/PrinterFallbackModal';

interface PrintLikeResult {
  success: boolean;
  message?: string;
}

interface FallbackState {
  isOpen: boolean;
  message: string;
  retry: ((printerId: number) => Promise<void>) | null;
}

const NO_PRINTER_PATTERNS = [
  'no printer configured',
  'ingen skriver konfigurert',
  'ingen printer konfigurert',
  // Printer ble resolved (f.eks. legacy UNC-sti fra profil/pakksone), men navnet
  // finnes ikke i PrintNode — operatøren skal få velge skriver manuelt da også.
  'no printnode printer id available',
];

function isNoPrinterError(message?: string): boolean {
  if (!message) return false;
  const lower = message.toLowerCase();
  return NO_PRINTER_PATTERNS.some((p) => lower.includes(p));
}

export function usePrinterFallback() {
  const [state, setState] = useState<FallbackState>({
    isOpen: false,
    message: '',
    retry: null,
  });

  const close = () => setState({ isOpen: false, message: '', retry: null });

  /**
   * Kjør en utskrift og fall tilbake til printer-velger-modal hvis ingen
   * fast printer er konfigurert.
   *
   * @param initial   Første forsøk (uten override)
   * @param retry     Re-forsøk med printer-override (bruker velger ID i modal)
   * @param onResult  Callback med endelig resultat (success eller failure)
   */
  async function tryPrint<T extends PrintLikeResult>(
    initial: () => Promise<T>,
    retry: (printerId: number) => Promise<T>,
    onResult: (result: T) => void
  ): Promise<void> {
    let result: T;
    try {
      result = await initial();
    } catch (err: any) {
      // Network/serializer-feil — ingen printer-fallback for dette.
      onResult({ success: false, message: err?.message || 'Ukjent feil' } as T);
      return;
    }

    if (!result.success && isNoPrinterError(result.message)) {
      setState({
        isOpen: true,
        message: result.message ?? '',
        retry: async (printerId: number) => {
          let retryResult: T;
          try {
            retryResult = await retry(printerId);
          } catch (err: any) {
            retryResult = { success: false, message: err?.message || 'Ukjent feil' } as T;
          }
          close();
          onResult(retryResult);
        },
      });
      return;
    }

    onResult(result);
  }

  const modal = (
    <PrinterFallbackModal
      isOpen={state.isOpen}
      message={state.message}
      onCancel={close}
      onConfirm={(printerId) => state.retry?.(printerId)}
    />
  );

  // isOpen eksponeres så kallstedet kan holde egen UI (f.eks. auto-lukk av
  // omsluttende modal) på vent mens operatøren velger skriver.
  return { tryPrint, modal, isOpen: state.isOpen };
}
